import React, { useState, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { frenchDateToIso, isoToFrenchDate } from '../../utils/dateUtils';

/**
 * Composant d'input de date avec calendrier natif et format français
 * Affiche jj/mm/aaaa mais utilise le calendrier natif du navigateur
 */
const DateInput = ({ value, onChange, placeholder = "jj/mm/aaaa", disabled, ...otherProps }) => {
    const [error, setError] = useState('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    const hiddenInputRef = useRef(null);

    // Convertir la valeur ISO en français pour l'affichage
    React.useEffect(() => {
        if (value && value.match(/^\d{4}-\d{2}-\d{2}/)) {
            setDisplayValue(isoToFrenchDate(value));
        } else {
            setDisplayValue(value || '');
        }
    }, [value]);

    const handleTextChange = (e) => {
        const inputValue = e.target.value;
        
        // Nettoyer l'input (enlever les caractères non numériques sauf /)
        const cleaned = inputValue.replace(/[^\d\/]/g, '');
        
        // Limiter à 10 caractères (jj/mm/aaaa)
        const limited = cleaned.slice(0, 10);
        
        setDisplayValue(limited);
        
        // Valider et convertir si c'est complet
        if (limited.length === 10) {
            const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            const match = limited.match(dateRegex);
            
            if (match) {
                const [, day, month, year] = match;
                const dayNum = parseInt(day, 10);
                const monthNum = parseInt(month, 10);
                const yearNum = parseInt(year, 10);
                
                // Vérifier si la date est valide
                const date = new Date(yearNum, monthNum - 1, dayNum);
                if (date.getDate() === dayNum && date.getMonth() === monthNum - 1 && date.getFullYear() === yearNum) {
                    const isoDate = frenchDateToIso(limited);
                    if (hiddenInputRef.current) {
                        hiddenInputRef.current.value = isoDate;
                    }
                    onChange({ ...e, target: { ...e.target, value: isoDate } });
                    setError('');
                } else {
                    setError('Date invalide');
                }
            } else {
                setError('Format invalide. Utilisez jj/mm/aaaa');
            }
        } else if (limited.length > 0) {
            setError('Format incomplet. Utilisez jj/mm/aaaa');
        } else {
            setError('');
            onChange({ ...e, target: { ...e.target, value: '' } });
        }
    };

    const handleCalendarChange = (e) => {
        const isoDate = e.target.value;
        if (isoDate) {
            const frenchDate = isoToFrenchDate(isoDate);
            setDisplayValue(frenchDate);
            onChange({ ...e, target: { ...e.target, value: isoDate } });
            setError('');
        } else {
            setDisplayValue('');
            onChange({ ...e, target: { ...e.target, value: '' } });
        }
    };

    const handleFocus = () => {
        setIsCalendarOpen(true);
    };

    const handleBlur = (e) => {
        // Délai pour permettre le clic sur le calendrier
        setTimeout(() => {
            setIsCalendarOpen(false);
        }, 200);
    };

    return (
        <div style={{ position: 'relative' }}>
            {/* Input visible avec format français */}
            <Form.Control
                type="text"
                value={displayValue}
                onChange={handleTextChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                isInvalid={!!error}
                style={{ paddingRight: '40px' }}
                {...otherProps}
            />
            
            {/* Input caché pour le calendrier natif */}
            <input
                ref={hiddenInputRef}
                type="date"
                value={value && value.match(/^\d{4}-\d{2}-\d{2}/) ? value : ''}
                onChange={handleCalendarChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 1
                }}
            />
            
            {/* Icône calendrier */}
            <div
                style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    zIndex: 2,
                    color: '#6c757d'
                }}
            >
                📅
            </div>
            
            {error && (
                <Form.Control.Feedback type="invalid">
                    {error}
                </Form.Control.Feedback>
            )}
        </div>
    );
};

export default DateInput;